// ===== SocialMediaManager.jsx - IMPROVED with multi-page display =====

import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Alert,
  Chip,
  Stack,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Avatar,
  Divider,
  Grid,
  Paper,
  useTheme,
  useMediaQuery
} from '@mui/material';

import {
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Facebook as FacebookIcon,
  Instagram as InstagramIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Close as CloseIcon,
  AccountCircle as AccountIcon,
  Business as BusinessIcon,
  OpenInNew as OpenInNewIcon
} from '@mui/icons-material';

import { useGetSocialAccountsQuery } from '../../redux/thunks/ai/getsocialaccounts';
import { useRemoveSocialAccountMutation } from '../../redux/thunks/ai/removesocialaccount';
import { useVerifySocialAccountMutation } from '../../redux/thunks/ai/verifysocialaccount';

import OAuthConnectionDialog from './OAuthConnectionDialog';

// Platform Icon Component
const PlatformIcon = ({ platform }) => {
  const icons = {
    facebook: FacebookIcon,
    instagram: InstagramIcon,
  };
  const IconComponent = icons[platform] || AccountIcon;
  return <IconComponent fontSize="small" />;
};

// Individual Page Card Component
const PageAccountCard = ({ account, onRemove, onVerify, isMobile }) => {
  const [isVerifying, setIsVerifying] = useState(false);

  const handleVerify = async () => {
    setIsVerifying(true);
    try {
      await onVerify(account.accountId);
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <Card 
      variant="outlined" 
      sx={{ 
        mb: 1.5,
        borderLeft: account.isHealthy ? '3px solid' : '1px solid',
        borderLeftColor: account.isHealthy ? 'success.main' : 'error.main'
      }}
    >
      <CardContent sx={{ p: isMobile ? 1.5 : 2, '&:last-child': { pb: isMobile ? 1.5 : 2 } }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
          {/* Avatar */}
          <Avatar 
            src={account.profilePicture} 
            alt={account.accountName}
            sx={{ width: 40, height: 40 }}
          >
            {account.accountName?.charAt(0)}
          </Avatar>
          
          {/* Info */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <PlatformIcon platform={account.platform} />
              <Typography 
                variant="body2" 
                fontWeight="600"
                sx={{ 
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {account.accountName}
              </Typography>
            </Box>
            
            <Typography 
              variant="caption" 
              color="text.secondary"
              sx={{ 
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              @{account.username}
            </Typography>

            {/* Status Chips */}
            <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5 }}>
              <Chip
                icon={account.isHealthy ? <CheckIcon /> : <ErrorIcon />}
                label={account.isHealthy ? 'Active' : 'Issue'}
                size="small"
                color={account.isHealthy ? 'success' : 'error'}
                sx={{ 
                  height: 20,
                  fontSize: '0.7rem'
                }}
              />
              
              {account.platform === 'facebook' && (
                <Chip
                  icon={<BusinessIcon />}
                  label="Page"
                  size="small"
                  variant="outlined"
                  sx={{ height: 20, fontSize: '0.7rem' }}
                />
              )}
              
              {account.platform === 'instagram' && (
                <Chip
                  label="Business"
                  size="small"
                  variant="outlined"
                  sx={{ height: 20, fontSize: '0.7rem' }}
                />
              )}
            </Stack>
          </Box>

          {/* Actions */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <IconButton
              size="small"
              onClick={handleVerify}
              disabled={isVerifying}
              title="Verify connection"
            >
              {isVerifying ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
            </IconButton>
            
            <IconButton
              size="small"
              onClick={() => onRemove(account.accountId)}
              color="error"
              title="Remove account"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
        
        {/* Error Message */}
        {!account.isHealthy && account.error && (
          <Alert severity="warning" sx={{ mt: 1.5, py: 0.5 }}>
            <Typography variant="caption">{account.error}</Typography>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};

// Platform Group Component
const PlatformGroup = ({ platform, accounts, onRemove, onVerify, isMobile }) => {
  const [expanded, setExpanded] = useState(true);
  
  const healthyCount = accounts.filter(acc => acc.isHealthy).length;
  const platformName = platform.charAt(0).toUpperCase() + platform.slice(1);
  
  const PlatformIconComponent = platform === 'facebook' ? FacebookIcon : InstagramIcon;
  const platformColor = platform === 'facebook' ? '#1877F2' : '#E4405F';

  return (
    <Paper variant="outlined" sx={{ mb: 2 }}>
      <Box
        sx={{
          p: 2,
          bgcolor: 'grey.50',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer'
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <PlatformIconComponent sx={{ color: platformColor }} />
          <Box>
            <Typography variant="subtitle2" fontWeight="600">
              {platformName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {accounts.length} page{accounts.length !== 1 ? 's' : ''}
            </Typography>
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip 
            label={`${healthyCount} active`}
            size="small" 
            color="success"
            sx={{ height: 22, fontSize: '0.7rem' }}
          />
          <IconButton size="small">
            <ExpandMoreIcon 
              sx={{ 
                transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.3s'
              }} 
            />
          </IconButton>
        </Box>
      </Box>
      
      {expanded && (
        <Box sx={{ p: 2 }}>
          {accounts.map((account) => (
            <PageAccountCard
              key={account.accountId}
              account={account}
              onRemove={onRemove}
              onVerify={onVerify}
              isMobile={isMobile}
            />
          ))}
        </Box>
      )}
    </Paper>
  );
};

// Main Component
const SocialMediaManager = ({ 
  businessId, 
  businessName, 
  isBusinessOwner,
  showAsAccordion = true,
  onOpenFullPage,
  navigationOnly = false,
  currentGooglePlaceId = ''
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const { data: socialAccounts, isLoading, refetch } = useGetSocialAccountsQuery();
  const [removeSocialAccount] = useRemoveSocialAccountMutation();
  const [verifySocialAccount] = useVerifySocialAccountMutation();
  
  const [showConnectionDialog, setShowConnectionDialog] = useState(false);
  const [isExpanded, setIsExpanded] = useState(!showAsAccordion);

  const accounts = socialAccounts?.accounts || [];
  const platformGroups = socialAccounts?.platformGroups || {};
  
  const healthyAccounts = accounts.filter(acc => acc.isHealthy);
  const totalAccounts = accounts.length;

  useEffect(() => {
    // Check for OAuth success
    const urlParams = new URLSearchParams(window.location.search);
    const oauthSuccess = urlParams.get('oauth_success');
    
    if (oauthSuccess === 'true') {
      console.log('✅ OAuth success detected - refetching accounts');
      refetch();
      // Clean URL
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
    }
  }, [refetch]);

  const handleRemoveAccount = async (accountId) => {
    if (window.confirm('Are you sure you want to remove this account?')) {
      try {
        await removeSocialAccount(accountId).unwrap();
      } catch (error) {
        console.error('Failed to remove account:', error);
        alert('Failed to remove account. Please try again.');
      }
    }
  };

  const handleVerifyAccount = async (accountId) => {
    try {
      await verifySocialAccount(accountId).unwrap();
    } catch (error) {
      console.error('Failed to verify account:', error);
      alert('Failed to verify account. Please try again.');
    }
  };

  if (!isBusinessOwner) return null;

  const content = (
    <Box>
      {/* Summary */}
      <Paper 
        elevation={0}
        sx={{ 
          p: isMobile ? 1.5 : 2,
          mb: 2,
          bgcolor: 'primary.lighter',
          border: '1px solid',
          borderColor: 'primary.light'
        }}
      >
        <Grid container spacing={isMobile ? 1 : 2}>
          <Grid item xs={6}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant={isMobile ? "h6" : "h5"} color="success.main" fontWeight="700">
                {healthyAccounts.length}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Active
              </Typography>
            </Box>
          </Grid>
          
          <Grid item xs={6}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant={isMobile ? "h6" : "h5"} color="primary.main" fontWeight="700">
                {totalAccounts}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Total
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Status Message */}
      {healthyAccounts.length > 0 ? (
        <Alert severity="success" icon={<CheckIcon />} sx={{ mb: 2 }}>
          <Typography variant={isMobile ? "caption" : "body2"}>
            {healthyAccounts.length} account{healthyAccounts.length !== 1 ? 's' : ''} ready for posting
          </Typography>
        </Alert>
      ) : totalAccounts > 0 ? (
        <Alert severity="warning" icon={<ErrorIcon />} sx={{ mb: 2 }}>
          <Typography variant={isMobile ? "caption" : "body2"}>
            Some accounts need attention. Verify connections to enable posting.
          </Typography>
        </Alert>
      ) : (
        <Alert severity="info" icon={<AccountIcon />} sx={{ mb: 2 }}>
          <Typography variant={isMobile ? "caption" : "body2"}>
            Connect your first account to start posting automatically
          </Typography>
        </Alert>
      )}

      {/* Platform Groups */}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress />
        </Box>
      ) : Object.keys(platformGroups).length > 0 ? (
        <Box sx={{ mb: 2 }}>
          {Object.entries(platformGroups).map(([platform, platformAccounts]) => (
            <PlatformGroup
              key={platform}
              platform={platform}
              accounts={platformAccounts}
              onRemove={handleRemoveAccount}
              onVerify={handleVerifyAccount}
              isMobile={isMobile}
            />
          ))}
        </Box>
      ) : (
        <Box sx={{ textAlign: 'center', py: 4, mb: 2 }}>
          <AccountIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Accounts Connected
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Connect your first account to start posting
          </Typography>
        </Box>
      )}

      {/* Action Buttons */}
      <Stack direction={isMobile ? "column" : "row"} spacing={isMobile ? 1 : 2}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setShowConnectionDialog(true)}
          fullWidth={isMobile}
        >
          Connect Account
        </Button>
        
        {totalAccounts > 0 && (
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => refetch()}
            fullWidth={isMobile}
          >
            Refresh
          </Button>
        )}
      </Stack>

      {/* Connection Dialog */}
      <OAuthConnectionDialog
        open={showConnectionDialog}
        onClose={() => setShowConnectionDialog(false)}
        businessId={businessId}
        businessName={businessName}
        currentGooglePlaceId={currentGooglePlaceId}
      />
    </Box>
  );

  if (showAsAccordion) {
    return (
      <Accordion>
        <AccordionSummary
          expandIcon={navigationOnly ? null : <ExpandMoreIcon />}
          onClick={navigationOnly ? (e) => { e.stopPropagation(); onOpenFullPage?.(); } : undefined}
          sx={navigationOnly ? { cursor: 'pointer' } : undefined}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
            <AccountIcon color="primary" fontSize="small" />
            <Typography variant="subtitle2" fontWeight="600">
              Integrations
            </Typography>
            <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
              {healthyAccounts.length > 0 && (
                <Chip 
                  label={healthyAccounts.length}
                  size="small" 
                  color="success"
                  sx={{ height: 20, minWidth: 20, fontSize: '0.7rem' }}
                />
              )}
              {onOpenFullPage && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenFullPage();
                  }}
                  title="Open full page"
                >
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          </Box>
        </AccordionSummary>
  
        {/* Only render content if not navigationOnly */}
        {!navigationOnly && (
          <AccordionDetails sx={{ pt: 0 }}>
            {content}
          </AccordionDetails>
        )}
      </Accordion>
    );
  }

  return content;
};

export default SocialMediaManager;